import { useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import {
  useExpenses,
  useClassSettings,
  useCollections,
  usePlannedExpenses,
} from "../../hooks/useData";
import { useAuth } from "../../context/AuthContext";
import {
  buildYearSettings,
  getSchoolYearLabel,
  useSchoolYear,
} from "../../context/SchoolYearContext";
import { supabase } from "../../lib/supabase";
import {
  calcBalance,
  calcPlannedExpenses,
  calcToReturn,
  calculateAvailableAfterPlanned,
  formatPLN,
  formatDate,
  statusColor,
} from "../../lib/finance";
import { exportExpensesPdf } from "../../lib/expensesPdf";
import ReceiptModal from "../ui/ReceiptModal";

const COLUMNS = [
  { key: "lp", label: "Lp." },
  { key: "occasion", label: "Okazja" },
  { key: "purchase_date", label: "Data" },
  { key: "item_or_service", label: "Towar / Usługa" },
  { key: "quantity", label: "Ilość" },
  { key: "unit_price", label: "Cena szt." },
  { key: "delivery_cost", label: "Dostawa" },
  { key: "total_value", label: "Łączna wartość" },
  { key: "confirmation_type", label: "Potwierdzenie" },
  { key: "cost_per_child", label: "Koszt/dziecko" },
  { key: "payout_date", label: "Data wypłaty" },
  { key: "payout_amount", label: "Kwota wypłaty" },
  { key: "status", label: "Status" },
  { key: "receipt_link", label: "Paragon" },
];

const STATUS_OPTIONS = [
  "Wszystkie",
  "Wypłacone",
  "Zakupione / Nie wypłacone",
  "Planowane",
  "Anulowane",
];

const EDIT_STATUS_OPTIONS = [
  "Wypłacone",
  "Zakupione / Nie wypłacone",
  "Planowane",
  "Anulowane",
];

const BULK_STATUS_OPTIONS = [
  "Wypłacone",
  "Zakupione / Nie wypłacone",
  "Planowane",
  "Anulowane",
];

function inputClass() {
  return "w-full rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-700 outline-none transition-colors focus:border-emerald-300";
}

function Field({ label, children, className = "" }) {
  return (
    <div className={className}>
      <label className="mb-2 block text-xs font-medium uppercase tracking-[0.14em] text-slate-400">
        {label}
      </label>
      {children}
    </div>
  );
}

function SectionCard({ children, className = "", delay = 0 }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay, duration: 0.4 }}
      className={`rounded-[30px] border border-white/70 bg-white/82 p-6 shadow-[0_18px_48px_rgba(15,23,42,0.06)] backdrop-blur-sm ${className}`}
    >
      {children}
    </motion.div>
  );
}

function toNumberOrNull(value) {
  if (value === "" || value === null || value === undefined) return null;
  const normalized = String(value).replace(",", ".");
  const parsed = Number(normalized);
  return Number.isNaN(parsed) ? null : parsed;
}

function buildInitialForm(expense) {
  if (!expense) return null;

  return {
    occasion: expense.occasion ?? "",
    purchase_date: expense.purchase_date ?? "",
    item_or_service: expense.item_or_service ?? "",
    quantity: expense.quantity ?? "",
    unit_price: expense.unit_price ?? "",
    delivery_cost: expense.delivery_cost ?? "",
    total_value: expense.total_value ?? "",
    confirmation_type: expense.confirmation_type ?? "",
    cost_per_child: expense.cost_per_child ?? "",
    payout_date: expense.payout_date ?? "",
    payout_amount: expense.payout_amount ?? "",
    status: expense.status ?? "Zakupione / Nie wypłacone",
    receipt_link: expense.receipt_link ?? "",
    payment_source: expense.payment_source ?? "class_fund",
    notes: expense.notes ?? "",
  };
}

export default function ExpensesScreen() {
  const { data: expenses = [], loading } = useExpenses();
  const { data: settings } = useClassSettings();
  const { data: collections = [] } = useCollections();
  const { data: planned = [] } = usePlannedExpenses();
  const { isAdmin } = useAuth();
  const {
    selectedSchoolYear,
    selectedSchoolYearId,
    isSelectedYearActive,
    isSelectedYearDraft,
    isSelectedYearArchived,
  } = useSchoolYear();
  const canWrite = isAdmin && (isSelectedYearActive || isSelectedYearDraft);
  const yearSettings = buildYearSettings(settings, selectedSchoolYear);
  const schoolYearLabel = getSchoolYearLabel(selectedSchoolYear);

  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("Wszystkie");
  const [sortKey, setSortKey] = useState("purchase_date");
  const [sortDir, setSortDir] = useState("desc");
  const [receiptUrl, setReceiptUrl] = useState(null);
  const [view, setView] = useState("table");

  const [editingExpense, setEditingExpense] = useState(null);
  const [editForm, setEditForm] = useState(null);
  const [editSubmitting, setEditSubmitting] = useState(false);
  const [editError, setEditError] = useState("");
  const [editMessage, setEditMessage] = useState("");

  const [selectedIds, setSelectedIds] = useState([]);
  const [bulkStatus, setBulkStatus] = useState("Wypłacone");
  const [bulkSubmitting, setBulkSubmitting] = useState(false);
  const [bulkError, setBulkError] = useState("");
  const [bulkMessage, setBulkMessage] = useState("");

  useEffect(() => {
    if (editingExpense) {
      setEditForm(buildInitialForm(editingExpense));
      setEditError("");
      setEditMessage("");
    } else {
      setEditForm(null);
      setEditError("");
      setEditMessage("");
    }
  }, [editingExpense]);

  const filtered = useMemo(() => {
    return expenses
      .filter((e) => {
        const q = search.toLowerCase();

        const matchSearch =
          !search ||
          (e.occasion ?? "").toLowerCase().includes(q) ||
          (e.item_or_service ?? "").toLowerCase().includes(q);

        const matchStatus =
          statusFilter === "Wszystkie" || e.status === statusFilter;

        return matchSearch && matchStatus;
      })
      .sort((a, b) => {
        const av = a[sortKey] ?? "";
        const bv = b[sortKey] ?? "";
        if (av < bv) return sortDir === "asc" ? -1 : 1;
        if (av > bv) return sortDir === "asc" ? 1 : -1;
        return 0;
      });
  }, [expenses, search, statusFilter, sortKey, sortDir]);

  useEffect(() => {
    setSelectedIds((prev) =>
      prev.filter((id) => expenses.some((expense) => expense.id === id))
    );
  }, [expenses]);

  async function handleExportPdf() {
    try {
      const activeCollection = collections.find(
        (c) => c.status === "active" || c.status === "aktywna"
      );

      const balance = calcBalance(filtered, yearSettings, collections);
      const toReturn = calcToReturn(filtered);
      const availableFunds = balance - toReturn;
      const plannedExpensesTotal = calcPlannedExpenses(planned);
      const availableAfterPlanned = calculateAvailableAfterPlanned({
        currentBalance: balance,
        reimbursementAmount: toReturn,
        plannedExpensesTotal,
      });

      await exportExpensesPdf({
        expenses: filtered,
        className: settings?.class_name ?? "Klasa",
        schoolYear: schoolYearLabel,
        title: "Rozliczenie wydatków",
        availableFunds,
        plannedExpensesTotal,
        availableAfterPlanned,
        reimbursementAmount: toReturn,
        activeCollectionName: activeCollection?.name ?? null,
        activeCollectionCurrentAmount: activeCollection?.current_amount ?? 0,
        activeCollectionTargetAmount: activeCollection?.target_amount ?? 0,
      });
    } catch (error) {
      console.error("Błąd eksportu PDF:", error);
      alert("Nie udało się wygenerować PDF.");
    }
  }

  function toggleSort(key) {
    if (sortKey === key) setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    else {
      setSortKey(key);
      setSortDir("asc");
    }
  }

  function SortIcon({ k }) {
    if (sortKey !== k) return <span className="ml-1 text-slate-400">↕</span>;
    return (
      <span className="ml-1 text-emerald-600">
        {sortDir === "asc" ? "↑" : "↓"}
      </span>
    );
  }

  function openEdit(expense) {
    if (!canWrite) return;
    setEditingExpense(expense);
  }

  function closeEdit() {
    if (editSubmitting) return;
    setEditingExpense(null);
  }

  function handleEditChange(e) {
    const { name, value } = e.target;

    setEditForm((prev) => {
      const next = {
        ...prev,
        [name]: value,
      };

      if (name === "status" && value === "Wypłacone") {
        if (!next.payout_date) {
          next.payout_date = new Date().toISOString().slice(0, 10);
        }
        if (!next.payout_amount) {
          next.payout_amount = next.total_value ?? "";
        }
      }

      return next;
    });
  }

  async function handleEditSubmit(e) {
    e.preventDefault();
    setEditError("");
    setEditMessage("");

    if (!editingExpense || !editForm) return;

    if (!canWrite) {
      setEditError("Rok tylko do odczytu.");
      return;
    }

    if (!editForm.occasion.trim()) {
      setEditError("Pole 'Okazja' jest wymagane.");
      return;
    }

    if (!editForm.item_or_service.trim()) {
      setEditError("Pole 'Towar / Usługa' jest wymagane.");
      return;
    }

    if (!editForm.purchase_date) {
      setEditError("Pole 'Data zakupu' jest wymagane.");
      return;
    }

    const payload = {
      occasion: editForm.occasion.trim(),
      purchase_date: editForm.purchase_date,
      item_or_service: editForm.item_or_service.trim(),
      quantity: toNumberOrNull(editForm.quantity),
      unit_price: toNumberOrNull(editForm.unit_price),
      delivery_cost: toNumberOrNull(editForm.delivery_cost),
      total_value: toNumberOrNull(editForm.total_value) ?? 0,
      confirmation_type: editForm.confirmation_type.trim() || null,
      cost_per_child: toNumberOrNull(editForm.cost_per_child),
      payout_date: editForm.payout_date || null,
      payout_amount: toNumberOrNull(editForm.payout_amount),
      status: editForm.status,
      receipt_link: editForm.receipt_link.trim() || null,
      payment_source: editForm.payment_source || "class_fund",
      notes: editForm.notes?.trim() || null,
    };

    if (payload.status === "Wypłacone" && !payload.payout_amount) {
      payload.payout_amount = payload.total_value;
    }

    try {
      setEditSubmitting(true);

      const { error } = await supabase
        .from("expenses")
        .update(payload)
        .eq("id", editingExpense.id)
        .eq("school_year_id", selectedSchoolYearId);

      if (error) {
        throw error;
      }

      setEditMessage("Wydatek został zaktualizowany.");

      setTimeout(() => {
        window.location.reload();
      }, 500);
    } catch (error) {
      setEditError(error.message || "Nie udało się zaktualizować wydatku.");
    } finally {
      setEditSubmitting(false);
    }
  }

  function toggleSelected(id) {
    setBulkError("");
    setBulkMessage("");
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  }

  function toggleSelectAllFiltered() {
    setBulkError("");
    setBulkMessage("");

    const filteredIds = filtered.map((e) => e.id);
    if (filteredIds.length === 0) return;

    const allSelected = filteredIds.every((id) => selectedIds.includes(id));

    if (allSelected) {
      setSelectedIds((prev) => prev.filter((id) => !filteredIds.includes(id)));
    } else {
      setSelectedIds((prev) => Array.from(new Set([...prev, ...filteredIds])));
    }
  }

  async function handleBulkStatusUpdate() {
    setBulkError("");
    setBulkMessage("");

    if (selectedIds.length === 0) {
      setBulkError("Najpierw zaznacz co najmniej jeden wydatek.");
      return;
    }

    try {
      if (!canWrite) {
        setBulkError("Rok tylko do odczytu.");
        return;
      }

      setBulkSubmitting(true);

      const selectedExpenses = expenses.filter((e) => selectedIds.includes(e.id));
      const today = new Date().toISOString().slice(0, 10);

      for (const expense of selectedExpenses) {
        const payload = {
          status: bulkStatus,
        };

        if (bulkStatus === "Wypłacone") {
          if (!expense.payout_date) {
            payload.payout_date = today;
          }
          if (!expense.payout_amount) {
            payload.payout_amount = Number(expense.total_value ?? 0);
          }
        }

        const { error } = await supabase
          .from("expenses")
          .update(payload)
          .eq("id", expense.id)
          .eq("school_year_id", selectedSchoolYearId);

        if (error) {
          throw error;
        }
      }

      setBulkMessage(`Zmieniono status dla ${selectedIds.length} rekordów.`);
      setSelectedIds([]);

      setTimeout(() => {
        window.location.reload();
      }, 500);
    } catch (error) {
      setBulkError(error.message || "Nie udało się zaktualizować statusów.");
    } finally {
      setBulkSubmitting(false);
    }
  }

  const filteredIds = filtered.map((e) => e.id);
  const allFilteredSelected =
    filteredIds.length > 0 &&
    filteredIds.every((id) => selectedIds.includes(id));

  if (loading) return <Loader />;

  return (
    <div className="space-y-6 lg:space-y-8">
      <motion.div
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
        className="rounded-[32px] border border-white/70 bg-[linear-gradient(135deg,rgba(255,255,255,0.82)_0%,rgba(236,253,245,0.72)_45%,rgba(255,247,237,0.76)_100%)] px-6 py-7 shadow-[0_18px_52px_rgba(15,23,42,0.06)]"
      >
        <div className="max-w-3xl">
          <div className="text-[11px] font-semibold uppercase tracking-[0.18em] text-emerald-700/80">
            Finanse
          </div>
          <h1 className="mt-3 text-3xl font-semibold tracking-tight text-slate-900 sm:text-4xl">
            Rozliczenie wydatków
          </h1>
          <p className="mt-3 max-w-2xl text-base leading-7 text-slate-600">
            Wyszukiwanie, filtrowanie, edycja oraz eksport raportu PDF dla wydatków klasowych.
          </p>
        </div>
      </motion.div>

      <SectionCard delay={0.08}>
        <div className="flex flex-col gap-4 xl:flex-row xl:items-center xl:justify-between">
          <div className="flex flex-1 flex-col gap-4 lg:flex-row lg:items-center">
            <div className="w-full max-w-sm">
              <input
                type="text"
                placeholder="Szukaj okazji, towaru..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className={inputClass()}
              />
            </div>

            <div className="flex flex-wrap gap-2">
              {STATUS_OPTIONS.map((s) => (
                <button
                  key={s}
                  onClick={() => setStatusFilter(s)}
                  className={`rounded-2xl px-4 py-2.5 text-xs font-semibold transition-all duration-200 ${
                    statusFilter === s
                      ? "bg-gradient-to-r from-emerald-500 to-teal-500 text-white shadow-[0_10px_24px_rgba(16,185,129,0.22)]"
                      : "border border-white/80 bg-white/75 text-slate-600 shadow-[0_8px_20px_rgba(15,23,42,0.04)] hover:bg-emerald-50 hover:text-emerald-700"
                  }`}
                >
                  {s}
                </button>
              ))}
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-2">
            <button
              type="button"
              onClick={handleExportPdf}
              className="rounded-2xl border border-white/80 bg-white/75 px-4 py-2.5 text-xs font-semibold text-slate-700 shadow-[0_8px_20px_rgba(15,23,42,0.04)] transition-colors hover:bg-emerald-50 hover:text-emerald-700"
            >
              Eksport PDF
            </button>

            <button
              onClick={() => setView("table")}
              className={`flex h-11 w-11 items-center justify-center rounded-2xl border text-sm transition-all ${
                view === "table"
                  ? "border-emerald-200 bg-emerald-50 text-emerald-700"
                  : "border-slate-200 bg-white text-slate-500 hover:bg-slate-50"
              }`}
            >
              ≡
            </button>
            <button
              onClick={() => setView("cards")}
              className={`flex h-11 w-11 items-center justify-center rounded-2xl border text-sm transition-all ${
                view === "cards"
                  ? "border-emerald-200 bg-emerald-50 text-emerald-700"
                  : "border-slate-200 bg-white text-slate-500 hover:bg-slate-50"
              }`}
            >
              ⊞
            </button>
          </div>
        </div>

        <div className="mt-4 text-sm text-slate-500">
          {filtered.length} z {expenses.length} wpisów
        </div>
      </SectionCard>

      {isAdmin && isSelectedYearDraft && (
        <SectionCard delay={0.1}>
          <div className="text-sm font-medium text-sky-800">
            Wersja robocza roku szkolnego. Dane mogą edytować wyłącznie administratorzy.
          </div>
        </SectionCard>
      )}

      {isAdmin && isSelectedYearArchived && (
        <SectionCard delay={0.1}>
          <div className="text-sm font-medium text-amber-800">
            Rok tylko do odczytu. Edycja wydatków i zmiana statusów są wyłączone.
          </div>
        </SectionCard>
      )}

      {canWrite && (
        <SectionCard delay={0.12}>
          <div className="flex flex-col gap-4 xl:flex-row xl:items-center xl:justify-between">
            <div className="text-sm text-slate-600">
              Zaznaczone:{" "}
              <span className="font-semibold text-emerald-700">{selectedIds.length}</span>
            </div>

            <div className="flex flex-1 flex-col gap-3 lg:flex-row lg:items-center lg:justify-end">
              <div className="w-full max-w-xs">
                <select
                  value={bulkStatus}
                  onChange={(e) => setBulkStatus(e.target.value)}
                  className={inputClass()}
                >
                  {BULK_STATUS_OPTIONS.map((status) => (
                    <option key={status} value={status}>
                      {status}
                    </option>
                  ))}
                </select>
              </div>

              <button
                type="button"
                onClick={handleBulkStatusUpdate}
                disabled={bulkSubmitting || selectedIds.length === 0}
                className={`rounded-2xl bg-gradient-to-r from-emerald-500 to-teal-500 px-5 py-3 text-sm font-semibold text-white shadow-[0_10px_24px_rgba(16,185,129,0.22)] transition-transform duration-200 ${
                  bulkSubmitting || selectedIds.length === 0
                    ? "cursor-not-allowed opacity-60"
                    : "hover:-translate-y-0.5"
                }`}
              >
                {bulkSubmitting ? "Zapisywanie..." : "Zmień status zaznaczonych"}
              </button>

              {selectedIds.length > 0 && (
                <button
                  type="button"
                  onClick={() => {
                    setSelectedIds([]);
                    setBulkError("");
                    setBulkMessage("");
                  }}
                  disabled={bulkSubmitting}
                  className="rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm font-semibold text-slate-600 transition-colors hover:bg-slate-50"
                >
                  Wyczyść zaznaczenie
                </button>
              )}
            </div>
          </div>

          <div className="mt-3 text-sm text-slate-500">
            Zbiorcza edycja zmienia tylko status. Pozostałe pola nadal edytujesz pojedynczo.
          </div>

          {bulkError && (
            <div className="mt-4 rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
              {bulkError}
            </div>
          )}

          {bulkMessage && (
            <div className="mt-4 rounded-2xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-700">
              {bulkMessage}
            </div>
          )}
        </SectionCard>
      )}

      {view === "table" && (
        <SectionCard delay={0.16} className="hidden lg:block overflow-hidden p-0">
          <div className="overflow-x-auto pb-3 scrollbar-thin scrollbar-thumb-slate-300 scrollbar-track-slate-100">
            <table className="w-full min-w-[1450px] text-sm">
              <thead>
                <tr className="border-b border-slate-100 bg-slate-50/70">
                  {canWrite && (
                    <th className="px-4 py-4 text-left text-xs font-semibold uppercase tracking-[0.14em] text-slate-400">
                      <input
                        type="checkbox"
                        checked={allFilteredSelected}
                        onChange={toggleSelectAllFiltered}
                        className="accent-emerald-500"
                        aria-label="Zaznacz wszystkie widoczne rekordy"
                      />
                    </th>
                  )}

                  {COLUMNS.map((col) => (
                    <th
                      key={col.key}
                      onClick={() =>
                        col.key !== "receipt_link" && toggleSort(col.key)
                      }
                      className={`px-4 py-4 text-left text-xs font-semibold uppercase tracking-[0.14em] text-slate-400 whitespace-nowrap ${
                        col.key !== "receipt_link"
                          ? "cursor-pointer select-none hover:text-slate-600"
                          : ""
                      }`}
                    >
                      {col.label}
                      {col.key !== "receipt_link" && col.key !== "lp" && (
                        <SortIcon k={col.key} />
                      )}
                    </th>
                  ))}

                  {canWrite && (
                    <th className="px-4 py-4 text-left text-xs font-semibold uppercase tracking-[0.14em] text-slate-400 whitespace-nowrap">
                      Akcje
                    </th>
                  )}
                </tr>
              </thead>

              <tbody>
                {filtered.map((e, i) => (
                  <tr
                    key={e.id}
                    className="border-b border-slate-100 transition-colors hover:bg-slate-50/70"
                  >
                    {canWrite && (
                      <td className="px-4 py-4">
                        <input
                          type="checkbox"
                          checked={selectedIds.includes(e.id)}
                          onChange={() => toggleSelected(e.id)}
                          className="accent-emerald-500"
                          aria-label={`Zaznacz wydatek ${e.occasion || i + 1}`}
                        />
                      </td>
                    )}

                    <td className="px-4 py-4 text-xs font-medium text-slate-400">
                      {i + 1}
                    </td>
                    <td className="max-w-[160px] truncate px-4 py-4 text-slate-700">
                      {e.occasion}
                    </td>
                    <td className="whitespace-nowrap px-4 py-4 text-xs text-slate-500">
                      {formatDate(e.purchase_date)}
                    </td>
                    <td className="max-w-[220px] truncate px-4 py-4 text-slate-800">
                      {e.item_or_service}
                    </td>
                    <td className="px-4 py-4 text-right text-slate-600">
                      {e.quantity ?? "—"}
                    </td>
                    <td className="px-4 py-4 text-right text-slate-700">
                      {e.unit_price ? formatPLN(e.unit_price) : "—"}
                    </td>
                    <td className="px-4 py-4 text-right text-slate-600">
                      {e.delivery_cost ? formatPLN(e.delivery_cost) : "—"}
                    </td>
                    <td className="px-4 py-4 text-right font-semibold text-slate-900">
                      {formatPLN(e.total_value)}
                    </td>
                    <td className="px-4 py-4 text-xs text-slate-500">
                      {e.confirmation_type ?? "—"}
                    </td>
                    <td className="px-4 py-4 text-right text-slate-700">
                      {e.cost_per_child ? formatPLN(e.cost_per_child) : "—"}
                    </td>
                    <td className="whitespace-nowrap px-4 py-4 text-xs text-slate-500">
                      {formatDate(e.payout_date)}
                    </td>
                    <td className="px-4 py-4 text-right text-slate-700">
                      {e.payout_amount ? formatPLN(e.payout_amount) : "—"}
                    </td>
                    <td className="px-4 py-4">
                      <span className={`badge ${statusColor(e.status)}`}>
                        {e.status}
                      </span>
                    </td>
                    <td className="px-4 py-4">
                      {e.receipt_link ? (
                        <button
                          onClick={() => setReceiptUrl(e.receipt_link)}
                          className="text-xs font-semibold text-emerald-700 underline underline-offset-2 transition-colors hover:text-emerald-600"
                        >
                          Podgląd
                        </button>
                      ) : (
                        <span className="text-slate-400">—</span>
                      )}
                    </td>

                    {canWrite && (
                      <td className="px-4 py-4">
                        <button
                          type="button"
                          onClick={() => openEdit(e)}
                          className="rounded-2xl border border-slate-200 bg-white px-4 py-2 text-xs font-semibold text-slate-700 transition-colors hover:bg-emerald-50 hover:text-emerald-700"
                        >
                          Edytuj
                        </button>
                      </td>
                    )}
                  </tr>
                ))}

                {filtered.length === 0 && (
                  <tr>
                    <td
                      colSpan={COLUMNS.length + (canWrite ? 2 : 0)}
                      className="py-14 text-center text-slate-500"
                    >
                      Brak wyników
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </SectionCard>
      )}

      {(view === "cards" || view === "table") && (
        <div className={`space-y-3 ${view === "table" ? "lg:hidden" : ""}`}>
          {filtered.map((e, i) => (
            <motion.div
              key={e.id}
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.04, duration: 0.25 }}
              className="rounded-[28px] border border-white/70 bg-white/82 p-5 shadow-[0_14px_36px_rgba(15,23,42,0.05)]"
            >
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0 flex-1">
                  <div className="text-base font-semibold text-slate-900">
                    {e.occasion || "Bez okazji"}
                  </div>
                  <div className="mt-1 text-sm text-slate-500">
                    {e.item_or_service || "Brak opisu"}
                  </div>
                </div>

                <span className={`badge ${statusColor(e.status)} shrink-0`}>
                  {e.status}
                </span>
              </div>

              <div className="mt-4 grid grid-cols-2 gap-3 text-sm">
                <div>
                  <span className="text-slate-400">Data: </span>
                  <span className="text-slate-700">{formatDate(e.purchase_date)}</span>
                </div>
                <div>
                  <span className="text-slate-400">Ilość: </span>
                  <span className="text-slate-700">{e.quantity ?? "—"}</span>
                </div>
                <div>
                  <span className="text-slate-400">Cena szt.: </span>
                  <span className="text-slate-700">
                    {e.unit_price ? formatPLN(e.unit_price) : "—"}
                  </span>
                </div>
                <div>
                  <span className="text-slate-400">Dostawa: </span>
                  <span className="text-slate-700">
                    {e.delivery_cost ? formatPLN(e.delivery_cost) : "—"}
                  </span>
                </div>
                <div>
                  <span className="text-slate-400">Koszt/dziecko: </span>
                  <span className="text-slate-700">
                    {e.cost_per_child ? formatPLN(e.cost_per_child) : "—"}
                  </span>
                </div>
                <div>
                  <span className="text-slate-400">Potwierdzenie: </span>
                  <span className="text-slate-700">
                    {e.confirmation_type ?? "—"}
                  </span>
                </div>
                {e.payout_date && (
                  <div>
                    <span className="text-slate-400">Wypłata: </span>
                    <span className="text-slate-700">{formatDate(e.payout_date)}</span>
                  </div>
                )}
                {e.payout_amount && (
                  <div>
                    <span className="text-slate-400">Kwota wypłaty: </span>
                    <span className="text-slate-700">{formatPLN(e.payout_amount)}</span>
                  </div>
                )}
              </div>

              <div className="mt-4 flex items-center justify-between gap-3 border-t border-slate-100 pt-4">
                <span className="text-xl font-semibold text-slate-900">
                  {formatPLN(e.total_value)}
                </span>

                <div className="flex items-center gap-2">
                  {e.receipt_link && (
                    <button
                      onClick={() => setReceiptUrl(e.receipt_link)}
                      className="rounded-2xl border border-slate-200 bg-white px-4 py-2 text-xs font-semibold text-slate-700 transition-colors hover:bg-emerald-50 hover:text-emerald-700"
                    >
                      Paragon
                    </button>
                  )}

                  {canWrite && (
                    <button
                      type="button"
                      onClick={() => openEdit(e)}
                      className="rounded-2xl border border-slate-200 bg-white px-4 py-2 text-xs font-semibold text-slate-700 transition-colors hover:bg-emerald-50 hover:text-emerald-700"
                    >
                      Edytuj
                    </button>
                  )}
                </div>
              </div>
            </motion.div>
          ))}

          {filtered.length === 0 && (
            <div className="rounded-[28px] border border-dashed border-slate-200 bg-slate-50/70 px-6 py-12 text-center text-slate-500">
              Brak wyników
            </div>
          )}
        </div>
      )}

      <ReceiptModal url={receiptUrl} onClose={() => setReceiptUrl(null)} />

      <EditExpenseModal
        open={!!editingExpense}
        form={editForm}
        submitting={editSubmitting}
        error={editError}
        message={editMessage}
        onClose={closeEdit}
        onChange={handleEditChange}
        onSubmit={handleEditSubmit}
      />
    </div>
  );
}

function EditExpenseModal({
  open,
  form,
  submitting,
  error,
  message,
  onClose,
  onChange,
  onSubmit,
}) {
  if (!open || !form) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/35 p-4 backdrop-blur-sm">
      <div className="max-h-[90vh] w-full max-w-5xl overflow-y-auto rounded-[30px] border border-white/70 bg-white/95 p-6 shadow-[0_24px_70px_rgba(15,23,42,0.18)]">
        <div className="flex items-start justify-between gap-4">
          <div>
            <div className="text-xl font-semibold tracking-tight text-slate-900">
              Edytuj wydatek
            </div>
            <div className="mt-1 text-sm text-slate-500">
              Zmień dane wydatku i zapisz aktualizację.
            </div>
          </div>

          <button
            type="button"
            onClick={onClose}
            disabled={submitting}
            className="flex h-10 w-10 items-center justify-center rounded-2xl bg-slate-100/80 text-slate-500 transition-colors hover:bg-slate-200 hover:text-slate-700"
          >
            ✕
          </button>
        </div>

        <form onSubmit={onSubmit} className="mt-6 space-y-5">
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-4">
            <Field label="Okazja">
              <input
                type="text"
                name="occasion"
                value={form.occasion}
                onChange={onChange}
                placeholder="Okazja"
                className={inputClass()}
              />
            </Field>

            <Field label="Data zakupu">
              <input
                type="date"
                name="purchase_date"
                value={form.purchase_date}
                onChange={onChange}
                className={inputClass()}
              />
            </Field>

            <Field label="Towar / Usługa">
              <input
                type="text"
                name="item_or_service"
                value={form.item_or_service}
                onChange={onChange}
                placeholder="Towar / Usługa"
                className={inputClass()}
              />
            </Field>

            <Field label="Status">
              <select
                name="status"
                value={form.status}
                onChange={onChange}
                className={inputClass()}
              >
                {EDIT_STATUS_OPTIONS.map((status) => (
                  <option key={status}>{status}</option>
                ))}
              </select>
            </Field>

            <Field label="Ilość">
              <input
                type="number"
                step="0.01"
                name="quantity"
                value={form.quantity}
                onChange={onChange}
                placeholder="Ilość"
                className={inputClass()}
              />
            </Field>

            <Field label="Cena szt.">
              <input
                type="number"
                step="0.01"
                name="unit_price"
                value={form.unit_price}
                onChange={onChange}
                placeholder="Cena szt."
                className={inputClass()}
              />
            </Field>

            <Field label="Dostawa">
              <input
                type="number"
                step="0.01"
                name="delivery_cost"
                value={form.delivery_cost}
                onChange={onChange}
                placeholder="Dostawa"
                className={inputClass()}
              />
            </Field>

            <Field label="Łączna wartość">
              <input
                type="number"
                step="0.01"
                name="total_value"
                value={form.total_value}
                onChange={onChange}
                placeholder="Łączna wartość"
                className={inputClass()}
              />
            </Field>

            <Field label="Potwierdzenie">
              <input
                type="text"
                name="confirmation_type"
                value={form.confirmation_type}
                onChange={onChange}
                placeholder="Potwierdzenie"
                className={inputClass()}
              />
            </Field>

            <Field label="Koszt / dziecko">
              <input
                type="number"
                step="0.01"
                name="cost_per_child"
                value={form.cost_per_child}
                onChange={onChange}
                placeholder="Koszt / dziecko"
                className={inputClass()}
              />
            </Field>

            <Field label="Data wypłaty">
              <input
                type="date"
                name="payout_date"
                value={form.payout_date}
                onChange={onChange}
                className={inputClass()}
              />
            </Field>

            <Field label="Kwota wypłaty">
              <input
                type="number"
                step="0.01"
                name="payout_amount"
                value={form.payout_amount}
                onChange={onChange}
                placeholder="Kwota wypłaty"
                className={inputClass()}
              />
            </Field>

            <Field label="Źródło płatności">
              <select
                name="payment_source"
                value={form.payment_source}
                onChange={onChange}
                className={inputClass()}
              >
                <option value="class_fund">class_fund</option>
                <option value="prywatne">prywatne</option>
              </select>
            </Field>
          </div>

          <Field label="Link do paragonu">
            <input
              type="text"
              name="receipt_link"
              value={form.receipt_link}
              onChange={onChange}
              placeholder="Link do paragonu / zdjęcia"
              className={inputClass()}
            />
          </Field>

          <Field label="Notatki">
            <textarea
              name="notes"
              value={form.notes}
              onChange={onChange}
              placeholder="Notatki"
              className={`${inputClass()} min-h-[110px] resize-y`}
            />
          </Field>

          {error && (
            <div className="rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
              {error}
            </div>
          )}

          {message && (
            <div className="rounded-2xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-700">
              {message}
            </div>
          )}

          <div className="flex flex-wrap justify-end gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              disabled={submitting}
              className="rounded-2xl border border-slate-200 bg-white px-4 py-2.5 text-sm font-semibold text-slate-600 transition-colors hover:bg-slate-50"
            >
              Anuluj
            </button>

            <button
              type="submit"
              disabled={submitting}
              className={`rounded-2xl bg-gradient-to-r from-emerald-500 to-teal-500 px-5 py-2.5 text-sm font-semibold text-white shadow-[0_10px_24px_rgba(16,185,129,0.22)] transition-transform duration-200 ${
                submitting ? "cursor-not-allowed opacity-60" : "hover:-translate-y-0.5"
              }`}
            >
              {submitting ? "Zapisywanie..." : "Zapisz zmiany"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function Loader() {
  return (
    <div className="flex items-center justify-center py-32">
      <div className="h-8 w-8 rounded-full border-2 border-emerald-400 border-t-transparent animate-spin" />
    </div>
  );
}
